import { useState } from "react"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"
import { Mail } from "lucide-react"

interface SendTestEmailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSendTest: (email: string) => Promise<void>
  defaultEmail?: string
}

export default function SendTestEmailDialog({
  open,
  onOpenChange,
  onSendTest,
  defaultEmail = ""
}: SendTestEmailDialogProps) {
  const [email, setEmail] = useState(defaultEmail)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when dialog opens
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      setEmail(defaultEmail)
      setError(null)
    }
    onOpenChange(newOpen)
  }

  const handleSend = async () => {
    setError(null)

    // Validate email
    if (!email.trim()) {
      setError("Email address is required")
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address")
      return
    }

    try {
      setIsSending(true)
      await onSendTest(email)
      // Close dialog on success
      onOpenChange(false)
    } catch (err: any) {
      // crm-api returns the real reason as { errors: [...] } / { error }. Surface
      // it instead of axios's opaque "Request failed with status code 4xx" — the
      // user needs to know it was e.g. a non-owned recipient or missing content.
      const data = err?.response?.data
      const msg =
        (Array.isArray(data?.errors) && data.errors[0]) ||
        data?.error ||
        err?.message ||
        "Failed to send test email"
      setError(msg)
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSending) {
      handleSend()
    }
  }

  return (
    <Dialog open={open} onClose={() => handleOpenChange(false)} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Box component="span" sx={{ display: "flex" }}>
          <Mail size={20} />
        </Box>
        Send Test Email
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          Send a test email to verify how your campaign will look. This will not save the campaign or send to all recipients.
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <TextField
              id="test-email"
              label="Email Address"
              type="email"
              size="small"
              fullWidth
              placeholder="example@domain.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={isSending}
              autoFocus
              InputLabelProps={{ shrink: true }}
            />
            {error && (
              <Typography variant="body2" sx={{ color: "error.main" }}>{error}</Typography>
            )}
          </Box>

          <Box sx={{ borderRadius: 1, bgcolor: (t) => alpha(t.palette.info.main, 0.12), border: 1, borderColor: (t) => alpha(t.palette.info.main, 0.3), p: 1.5 }}>
            <Typography variant="body2" sx={{ color: "info.dark" }}>
              <strong>Note:</strong> The test email will be sent immediately to the address you specify. The campaign will not be saved or sent to your recipients.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={() => onOpenChange(false)}
          disabled={isSending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isSending || !email.trim()}
          startIcon={isSending ? <CircularProgress size={16} color="inherit" /> : <Mail size={16} />}
        >
          {isSending ? "Sending..." : "Send Test"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
